/**
 * Centralized prompts for every AI call in the Setup wizard. Keeping them
 * here makes them easy to tweak, A/B-test, or version without hunting through
 * step components.
 *
 * Each export returns an `AIRequest` — the shape `complete()` / `run()` in
 * `./ai.ts` accepts. Most include a `studyContext` block which is sent under
 * a separate system message and cached by Anthropic prompt caching, so the
 * model doesn't re-pay for the same context across multiple wizard steps.
 */

import { Study } from './study';

export type AIRequest = {
  prompt: string;
  studyContext?: string;
  maxTokens?: number;
};

const goalContext = (goal: string) => `Study goal: ${goal}`;

const fullStudyContext = (
  study: Pick<Study, 'goal' | 'interests' | 'background'>,
) =>
  [
    `Study goal: ${study.goal}`,
    `Interests: ${study.interests.join('; ')}`,
    study.background ? `Background: ${study.background}` : '',
  ]
    .filter(Boolean)
    .join('\n');

/* ────────────────────────────────────────────────────────────
   Step 2 — Research Purpose
   Generate 4 discipline-tagged options for how the findings
   will be used, tailored to the study goal.
   Expected response: JSON array of { label, desc }
   ──────────────────────────────────────────────────────────── */
export const decisionOptions = (goal: string): AIRequest => ({
  prompt: `You are a research design expert. Based on this study goal, identify the 3 most relevant academic disciplines that would use qualitative interviews to study this topic, then generate one research purpose option per discipline, plus one exploratory option (4 total).

Study goal: "${goal}"

Instructions:
- First, think about which disciplines would naturally conduct qualitative interviews on this topic (e.g. Psychology, Sociology, HCI, Communication, Education, Public Health, Anthropology, Management, STS, Political Science, etc.)
- Pick the 3 most relevant disciplines for THIS specific study goal
- For each, write a purpose that reflects how that discipline would use the findings
- Always add a 4th option: "Explore the phenomenon openly — no specific outcome yet (Exploratory)"

Style:
- Start each label with a verb: Inform, Understand, Examine, Explore, Contribute, Investigate...
- Include the discipline tag in parentheses at end of label, e.g. (Psychology), (HCI), (Public Health)
- Labels ~8-10 words. Descriptions: one sentence, specific to the study topic
- Not too specific — avoid naming exact systems or institutions

Return ONLY a JSON array of 4 objects with "label" and "desc". No explanation.`,
  studyContext: goalContext(goal),
});

/* ────────────────────────────────────────────────────────────
   Step 3 — Key Aspects (dynamic title)
   Rewrite the page title to reference the specific study topic.
   Expected response: a single rewritten sentence.
   ──────────────────────────────────────────────────────────── */
export const aspectTitle = (goal: string): AIRequest => ({
  prompt: `Rewrite this question to make it more specific to the study topic, while keeping the same meaning and structure:

Original: "What are the main aspects you want to learn from this study?"

Study goal: "${goal}"

Rules:
- Keep the same sentence structure: "What aspects of [TOPIC] are you most interested in learning from this study?"
- Replace [TOPIC] with a concise phrase that captures the study subject (2-5 words)
- Do not add extra words or change the meaning
- Return ONLY the rewritten question, nothing else`,
  studyContext: goalContext(goal),
  maxTokens: 200,
});

/* ────────────────────────────────────────────────────────────
   Step 3 — Key Aspects (initial suggestions)
   Suggest 3 core interview-guide aspects.
   Expected response: JSON array of 3 strings.
   ──────────────────────────────────────────────────────────── */
export const aspectSuggestions = (goal: string): AIRequest => ({
  prompt: `You are a qualitative research expert helping design a semi-structured interview.

Study goal: ${goal}

Suggest exactly 3 core interview guide aspects — the main topics the interview should cover. These are NOT questions, just topic areas written in plain language (e.g. "Daily usage patterns", "Emotional impact on relationships").

Return ONLY a JSON array of 3 strings, e.g. ["Aspect 1", "Aspect 2", "Aspect 3"]`,
  studyContext: goalContext(goal),
});

/* ────────────────────────────────────────────────────────────
   Step 5 — Draft Questions
   Generate N open-ended interview questions.
   Expected response: JSON array of strings.
   ──────────────────────────────────────────────────────────── */
export const draftQuestions = (
  study: Pick<Study, 'goal' | 'interests' | 'background'>,
  count: number,
): AIRequest => ({
  prompt: `You are a qualitative research expert. Generate ${count} semi-structured interview questions based on:

Study Goal: ${study.goal}

Research Interests:
${study.interests.map((r, i) => `${i + 1}. ${r}`).join('\n')}

${study.background ? `Background: ${study.background}` : ''}

Generate exactly ${count} open-ended interview questions. Each should:
- Be open-ended and conversational
- Address the research interests
- Be appropriate for a 20-min interview
- Flow naturally from broad to specific

Return ONLY a JSON array of strings, e.g. ["Question 1?", "Question 2?", ...]`,
  studyContext: fullStudyContext(study),
  maxTokens: 2048,
});

/* ────────────────────────────────────────────────────────────
   Step 10 — Participant Brief (draft all sections)
   Write a short plain-language paragraph for each brief section.
   Expected response: JSON array of strings (one per section, in order).
   ──────────────────────────────────────────────────────────── */
export const briefSections = (
  study: Pick<Study, 'goal' | 'interests' | 'background'>,
  sections: Array<{ title: string; hint?: string }>,
): AIRequest => ({
  prompt: `You are helping a researcher write a participant information sheet for a qualitative voice interview study. For each section listed, write a SHORT plain-language paragraph (2-4 sentences). Address the participant directly as "you". Warm, clear, not legalistic.

Study goal: ${study.goal}
Research interests: ${study.interests.join('; ')}
${study.background ? `Background: ${study.background}` : ''}
Length: ~20 min voice conversation with an AI interviewer.

Sections to fill (in order):
${sections.map((s, i) => `${i + 1}. ${s.title}${s.hint ? ' — ' + s.hint : ''}`).join('\n')}

Return ONLY a JSON array of strings (one per section, in the same order). No explanation.`,
  studyContext: fullStudyContext(study),
  maxTokens: 3000,
});
